import { viewportMobileRanges } from '../../utils/productCardImages';

export const mapVariants = (variants) => {
  return variants?.map((variant) => {
    return {
      ...variant,
      Sellable: Number(variant.inventory) > 0,
      imgs: variant.image_url,
      Color: variant.option1,
      id: variant.variant_id,
      'Product-sku': variant.sku ? variant.sku : '',
    };
  });
};

/**
 * Evaluates the product type inside of product card through a specific string word
 * @param {String} productType - the whole product type name
 * @param {String} word - a lower_case word to seek
 * @returns {Boolean} - return true or false depending on result
 */
export const isProductType = (productType, word) => {
  return productType?.toLowerCase().includes(word);
};

/**
 * Provide a consistent url that can handle multiple sections, changes in the variant property
 * @param {Array[Object]} sectionsToRender - sections that you will render
 * @param {string} baseUrl - base url to build upon
 * @returns {string} - a HTTPS url
 */
export function buildSectionUrl(sectionsToRender, baseUrl) {
  return `
    ${baseUrl}&sections=${sectionsToRender.map((sectionItem) => sectionItem.section).join(',')}`;
}

/**
 * Extracts the inner HTML of a section from a given HTML string using a selector.
 *
 * @param {string} html - The HTML string to parse.
 * @param {string} selector - The CSS selector to find the section.
 * @returns {string} The inner HTML of the selected section.
 */
export function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
}

/**
 * Generates an IntersectionObserver callback for lazy-loading images.
 * Loads images when their anchor wrappers become visible in the viewport.
 *
 * @param {boolean} [trackFalsePositive=false] - Whether to track false positives for zoom gallery. It's optional
 * @returns {Function} IntersectionObserver callback.
 */
export const generateImageObserverCallback = (trackFalsePositive = false) => {
  return (entries, observer) => {
    entries.forEach((entry) => {
      // Track a false positve on zoom gallery
      if (trackFalsePositive) {
        if (entry.isIntersecting && entry.intersectionRatio == 1 && entry.target.style.order !== '1') {
          return;
        }
      }

      if (entry.isIntersecting) {
        // Intersect image element
        const anchor = entry.target;
        // Disconnect observer if an anchor already contains an image
        if (!Object.keys(anchor.dataset).length) {
          return observer.disconnect();
        }

        // Build a picture element
        const pictureElement = document.createElement('picture');
        const imageElement = document.createElement('img');
        const mediaConditions = anchor.dataset.sizes?.split(',');
        const srcsetArray = anchor.dataset.srcset?.split('|');
        delete anchor.dataset['sizes'];
        delete anchor.dataset['srcset'];

        mediaConditions?.forEach((mediaCondition, index) => {
          const sourceElement = document.createElement('source');
          sourceElement.media = mediaCondition;
          sourceElement.srcset = srcsetArray[index];
          // Append every source element in picture element
          pictureElement.appendChild(sourceElement);
        });

        // Append image element in picture element
        pictureElement.appendChild(imageElement);

        // This scans all data-* attributes and set each image attribute. Then, removes each data-* attribute
        for (let key in anchor.dataset) {
          imageElement[key] = anchor.dataset[key];
          delete anchor.dataset[key];
        }

        // Appends image element into anchor and set an onload function
        imageElement.onload = () => observer.disconnect();
        imageElement.onerror = imageErrorHandler;
        anchor.appendChild(pictureElement);

        // Preload potential next and previous images in the standard and zoom galleries
        preloadImages(anchor);
      }
    });
  };
};

/**
 * Preloads images for the product recommendation gallery.
 * @param {HTMLDivElemente | HTMLAnchorElement} anchor - the anchor container element
 */
export const preloadImages = (anchor) => {
  const nodes = Array.from(anchor.closest('.carousel-c-scroll__container')?.querySelectorAll('a[id*="carousel_"]'));
  const currentIndex = nodes.findIndex((node) => node.id === anchor.id);
  let nextEl = null;

  // Select next elements
  nextEl = nodes[currentIndex + 1];

  // Build and link elements on the head for preloading them whenever they exist
  nextEl && Object.keys(nextEl.dataset).length && buildLink(nextEl);
};

/**
 * Extracts data information and builds a preload link element for a given image container.
 * By default, this will preload the first image element visible in image container.
 * This won't preload second media resource.
 * @param {HTMLDivElement} el - The anchor container element.
 */
const buildLink = (el) => {
  const link = document.createElement('link');
  const sizesParts = el.dataset.sizes.split(',');
  const srcSetParts = el.dataset.srcset.split('|');

  let index = 0;
  for (const [idx, range] of Object.entries(viewportMobileRanges)) {
    if (window.innerWidth < range[1] && window.innerWidth > range[0]) {
      index = Number(idx);
      break;
    }
  }

  // if there is no valid index, we will choose sizes for screen bigger than 1800 by default
  let sizes = index > 0 ? sizesParts[index] : sizesParts.at(-1);
  let srcSet = index > 0 ? srcSetParts[index] : srcSetParts.at(-1);

  link.rel = 'preload';
  link.as = 'image';
  link.imageSizes = sizes;
  link.imageSrcset = srcSet;
  document.getElementsByTagName('head')[0].appendChild(link);
};

/**
 * Generates options for the IntersectionObserver used for images.
 *
 * @param {HTMLElement|null} rootElement - The root element for the observer.
 * @returns {Object} IntersectionObserver options.
 */
export const generateImageObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.1, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};

/**
 * Generates an error handler for image loading errors.
 * Hides the image and adds an 'image-error' class to the closest anchor element.
 *
 * @returns {Function} Image error event handler.
 */
export function imageErrorHandler() {
  this.style.display = 'none';
  this.closest('a,div').classList.add('image-error');
}
